import { z } from 'zod';
import { safeProjectPath } from './project-artifact.js';
import { excludedProjectFileSchema, workspaceContextSchema } from './workspace-runtime.js';
/** Bytes of one editor buffer carried in an observation or returned by an inspection. */
export const OBSERVATION_FILE_BYTES = 65_536;
/** Combined bytes of the active buffer and every dirty buffer in one observation. */
export const OBSERVATION_BUFFER_BYTES = 131_072;
/** Bridge socket request cap for one observation body. */
export const OBSERVATION_BODY_BYTES = 262_144;
/** Combined bytes of file contents returned by one inspection. */
export const INSPECTION_BYTES = 262_144;
export const OBSERVATION_MAX_OPEN_FILES = 30;
export const OBSERVATION_MAX_DIRTY_FILES = 5;
export const OBSERVATION_MAX_DIAGNOSTICS = 30;
export const OBSERVATION_MAX_EXECUTIONS = 8;
export const OBSERVATION_OUTPUT_HEAD_CHARS = 1000;
export const OBSERVATION_OUTPUT_TAIL_CHARS = 3000;
export const INSPECTION_MAX_PATHS = 20;
const relativePath = z
    .string()
    .min(1)
    .max(256)
    .refine(safeProjectPath, 'Only project files can be observed');
const line = z.number().int().positive();
const column = z.number().int().positive();
const count = z.number().int().nonnegative();
const bufferContent = z.string().max(OBSERVATION_FILE_BYTES);
const byteLength = (text) => new TextEncoder().encode(text).byteLength;
export const observedFileSchema = z.object({
    path: relativePath,
    languageId: z.string().max(64),
    dirty: z.boolean(),
    documentVersion: count,
    lineCount: count,
    cursor: z.object({ line, column }),
    selection: z
        .object({
        startLine: line,
        startColumn: column,
        endLine: line,
        endColumn: column,
        text: z.string().max(2000),
    })
        .nullable(),
    visibleRange: z.object({ startLine: line, endLine: line }).optional(),
    content: bufferContent,
    truncated: z.boolean(),
});
export const observedTerminalExecutionSchema = z.object({
    id: z.string().min(1).max(64),
    command: z.string().max(500),
    /** Relative to the activity root (`.` for the root itself); omitted outside it. */
    cwd: z
        .string()
        .max(256)
        .refine((value) => !value.startsWith('/'), 'Terminal paths are relative')
        .optional(),
    startedAt: z.iso.datetime(),
    endedAt: z.iso.datetime().optional(),
    exitCode: z.number().int().optional(),
    running: z.boolean(),
    output: z.object({
        head: z.string().max(OBSERVATION_OUTPUT_HEAD_CHARS),
        tail: z.string().max(OBSERVATION_OUTPUT_TAIL_CHARS),
        truncated: z.boolean(),
        bytes: count,
    }),
});
/**
 * What the editor extension reports about the learner's IDE. Bounded, memory-only on the
 * VM, and untrusted evidence everywhere else. Unknown fields from a newer runtime are
 * stripped rather than rejected so an older API keeps working with a newer VM.
 */
export const editorObservationSchema = z
    .object({
    version: z.literal(1),
    capturedAt: z.iso.datetime(),
    context: workspaceContextSchema,
    activeFile: observedFileSchema.nullable(),
    openFiles: z
        .array(z.object({ path: relativePath, dirty: z.boolean(), active: z.boolean() }))
        .max(OBSERVATION_MAX_OPEN_FILES),
    dirtyFiles: z
        .array(z.object({
        path: relativePath,
        documentVersion: count,
        content: bufferContent,
        truncated: z.boolean(),
    }))
        .max(OBSERVATION_MAX_DIRTY_FILES),
    diagnostics: z
        .array(z.object({
        path: relativePath,
        line,
        column: column.optional(),
        severity: z.enum(['error', 'warning', 'information', 'hint']),
        message: z.string().max(500),
        source: z.string().max(64).optional(),
    }))
        .max(OBSERVATION_MAX_DIAGNOSTICS),
    terminal: z.object({
        executions: z.array(observedTerminalExecutionSchema).max(OBSERVATION_MAX_EXECUTIONS),
    }),
    omitted: z.object({ outsideRoot: count, unsafe: count, untitled: count, unsupported: count }),
})
    .superRefine((observation, ctx) => {
    const buffers = [
        ...(observation.activeFile ? [observation.activeFile.content] : []),
        ...observation.dirtyFiles.map((file) => file.content),
    ];
    if (buffers.some((content) => byteLength(content) > OBSERVATION_FILE_BYTES))
        ctx.addIssue({ code: 'custom', message: 'An observed buffer exceeds its byte budget' });
    if (buffers.reduce((sum, content) => sum + byteLength(content), 0) > OBSERVATION_BUFFER_BYTES)
        ctx.addIssue({
            code: 'custom',
            path: ['dirtyFiles'],
            message: 'Observed buffers exceed the combined byte budget',
        });
});
/** Bridge reply for the API: the latest observation of the controlling editor session, if any. */
export const editorObservationStatusSchema = z.object({
    connected: z.boolean(),
    receivedAt: z.iso.datetime().nullable(),
    observation: editorObservationSchema.nullable(),
});
/** Side-effect-free read of the selected activity: a tree and bounded file contents. */
export const inspectActivityRequestSchema = workspaceContextSchema
    .extend({
    paths: z.array(relativePath).max(INSPECTION_MAX_PATHS).optional(),
    includeTree: z.boolean().optional(),
})
    .strict();
export const inspectedFileSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('ok'),
        path: relativePath,
        content: bufferContent,
        truncated: z.boolean(),
        /** Size on disk, which can exceed the returned content. */
        bytes: count,
    }),
    z.object({
        status: z.literal('unavailable'),
        path: relativePath,
        reason: z.enum([
            'not_found',
            'excluded',
            'unsupported',
            'too_large',
            'changed',
            'binary',
            'budget',
        ]),
    }),
]);
export const activityInspectionSchema = z.object({
    tree: z
        .object({
        files: z.array(z.object({ path: relativePath, bytes: count })).max(200),
        excluded: z.array(excludedProjectFileSchema).max(500),
    })
        .nullable(),
    files: z.array(inspectedFileSchema).max(INSPECTION_MAX_PATHS),
});
